/*
 * <dropdown-sheet> — bottom-sheet presentation for the shared dropdown shell
 * (snippets/dropdown-menu.liquid, rendered when the caller passes `sheet_title`).
 *
 * The design system draws a menu selection as a bottom sheet below 1280px and as a dropdown at
 * and above it (Figma bottomSheet 2910:17926 — "1279px 이하의 환경에서 메뉴를 통해 여러 옵션 중
 * 선택하는 경우"). Both presentations are the *same* <details> and the same <ul>; only the
 * painting differs (component-dropdown-menu.css), so open/close, focus-out-to-close and any
 * selection wiring (e.g. assets/script-select-dropdown.js) keep working untouched.
 *
 * This element only adds what CSS cannot express:
 *   - keeping the panel open when a press lands on a non-focusable part of it (see below)
 *   - body scroll lock while the sheet is up (theme convention: body.overflow-hidden)
 *   - tap the scrim to dismiss
 *   - drag the sheet down to dismiss (the handlebar in the design is the affordance)
 * The last three are no-ops at >= 1280px, where the sheet is styled back into a dropdown.
 */
if (!customElements.get('dropdown-sheet')) {
  customElements.define(
    'dropdown-sheet',
    class DropdownSheet extends HTMLElement {
      // Sheets currently holding the body scroll lock, so two of them can't fight over it.
      static locks = new Set();

      // Drag past this many pixels and releasing dismisses instead of snapping back.
      static DISMISS_THRESHOLD = 80;

      connectedCallback() {
        this.details = this.closest('details');
        if (!this.details) return;

        this.summary = this.details.querySelector('summary');
        this.scrim = this.details.querySelector('.dropdown-menu__scrim');
        this.desktop = window.matchMedia('(min-width: 1280px)');

        this.onToggle = this.onToggle.bind(this);
        this.onScrimClick = this.onScrimClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);

        this.details.addEventListener('toggle', this.onToggle);
        if (this.scrim) this.scrim.addEventListener('click', this.onScrimClick);
        this.addEventListener('pointerdown', this.onPointerDown);
        this.desktop.addEventListener('change', this.onBreakpointChange);
      }

      disconnectedCallback() {
        this.details?.removeEventListener('toggle', this.onToggle);
        this.scrim?.removeEventListener('click', this.onScrimClick);
        this.removeEventListener('pointerdown', this.onPointerDown);
        this.desktop?.removeEventListener('change', this.onBreakpointChange);
        if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
        this.releaseLock();
      }

      /* ---- Open / close ----------------------------------------------------- */

      onToggle() {
        this.resetDrag();
        if (this.details.open && !this.desktop.matches) {
          this.takeLock();
          // A sheet covers the page, so Escape has to dismiss it. <details> has no such behavior of
          // its own, and the listener is on the document because focus stays on the trigger behind
          // the scrim until the shopper tabs into the list.
          document.addEventListener('keydown', this.onKeydown);
        } else {
          this.releaseLock();
          document.removeEventListener('keydown', this.onKeydown);
        }
      }

      onScrimClick() {
        this.close();
      }

      onKeydown(event) {
        if (event.key !== 'Escape') return;
        this.close();
      }

      // Same as DetailsDisclosure.close() (assets/script-details-disclosure.js) — kept in step so
      // a sheet dismissed by scrim, Escape or drag leaves the trigger in the state a focus-out close
      // would.
      close() {
        // The panel can be holding focus (onPointerDown), and hiding a focused element drops focus
        // to <body> — hand it back to the trigger the sheet came from instead.
        const hadFocus = this.contains(document.activeElement);

        this.details.removeAttribute('open');
        if (!this.summary) return;
        this.summary.setAttribute('aria-expanded', 'false');
        if (hadFocus) this.summary.focus({ preventScroll: true });
      }

      // Crossing to desktop turns the sheet back into a dropdown, which must not hold the page or
      // answer Escape. onToggle() already derives all of that from (open, breakpoint).
      onBreakpointChange() {
        this.onToggle();
      }

      /* ---- Body scroll lock ------------------------------------------------- */

      takeLock() {
        DropdownSheet.locks.add(this);
        document.body.classList.add('overflow-hidden');
      }

      releaseLock() {
        if (!DropdownSheet.locks.delete(this)) return;
        if (DropdownSheet.locks.size === 0) document.body.classList.remove('overflow-hidden');
      }

      /* ---- Keeping focus inside the panel / drag to dismiss ------------------ */

      onPointerDown(event) {
        // DetailsDisclosure closes on any focusout that lands outside the disclosure
        // (script-details-disclosure.js), and pressing something non-focusable — the scroll track,
        // the gap between two rows, the sheet's own padding — blurs the trigger to <body>. The
        // shopper pressed to scroll, so that must not read as a dismissal: taking focus here keeps
        // document.activeElement inside the disclosure, and DetailsDisclosure leaves the menu alone.
        // It works because the panel carries `tabindex="-1"` (dropdown-menu.liquid), which is also
        // what makes the browser land on the panel by itself. A press on an option still ends up
        // focusing that option — the browser's own focus move is the default action of this event,
        // so it runs after this listener.
        this.focus({ preventScroll: true });

        // Only the handlebar and header start a drag — they are the two elements CSS gives
        // `touch-action: none`, and the list below them has to stay scrollable.
        if (this.desktop.matches || event.button !== 0) return;
        if (!event.target.closest('.dropdown-menu__handlebar, .dropdown-menu__sheet-header')) return;

        this.dragStartY = event.clientY;
        this.dragOffset = 0;
        this.style.transition = 'none';

        // Capture so the drag survives the pointer leaving the sheet on the way down.
        this.setPointerCapture(event.pointerId);
        this.addEventListener('pointermove', this.onPointerMove);
        this.addEventListener('pointerup', this.onPointerUp);
        this.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(event) {
        if (this.dragStartY === undefined) return;
        // Downward only: dragging up must not lift the sheet off the bottom edge.
        this.dragOffset = Math.max(0, event.clientY - this.dragStartY);
        this.style.transform = `translateY(${this.dragOffset}px)`;
      }

      onPointerUp() {
        const dismiss = this.dragOffset > DropdownSheet.DISMISS_THRESHOLD;

        this.removeEventListener('pointermove', this.onPointerMove);
        this.removeEventListener('pointerup', this.onPointerUp);
        this.removeEventListener('pointercancel', this.onPointerUp);
        this.dragStartY = undefined;

        if (dismiss) {
          this.close(); // resetDrag() follows from the toggle this fires
          return;
        }

        // Not far enough — slide back down to rest.
        this.style.transition = '';
        this.style.transform = '';
        this.dragOffset = 0;
      }

      resetDrag() {
        this.style.transition = '';
        this.style.transform = '';
        this.dragOffset = 0;
        this.dragStartY = undefined;
      }
    }
  );
}
